import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, X, Save, GripVertical } from 'lucide-react';
import FileUploader from './FileUploader';
import { getImageUrl } from '../../lib/utils';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const SortableProductCard = ({ product, isSelected, onSelect, onDelete, formatPrice }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: product.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 p-3 border rounded cursor-pointer transition-colors ${
        isSelected ? 'border-black bg-gray-50' : 'hover:bg-gray-50'
      } ${isDragging ? 'shadow-lg' : ''}`}
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-100 rounded"
      >
        <GripVertical size={16} className="text-gray-400" />
      </div>
      <div onClick={() => onSelect(product)} className="flex items-center gap-3 flex-1 min-w-0">
        <img
          src={getImageUrl(product.image)}
          alt={product.name}
          className="w-12 h-12 rounded object-cover bg-gray-100"
          onError={(e) => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/48?text=V'; }}
        />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{product.name}</p>
          <p className="text-sm text-gray-500">{formatPrice(product.price)}</p>
        </div>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete(product.id);
        }}
        className="p-1 text-red-500 hover:bg-red-50 rounded"
      >
        <Trash2 size={16} />
      </button>
    </div>
  );
};

const ProductManager = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: '',
    altName: '',
    price: 0,
    material: '%100 Has İpek',
    dimensions: '65x65 cm',
    description: '',
    features: '',
    colors: '',
    sizes: '65x65',
    collection: 'kokten-isiga',
    images: [],
    isNew: true,
    isBestseller: false
  });

  // Available collections (must match slugs used on CollectionsPage.jsx)
  const collectionOptions = [
    { slug: 'kokten-isiga', name: 'Kökten Işığa' },
    { slug: 'turk-motifleri', name: 'Türk Motifleri' },
    { slug: 'yaz-masali', name: 'Yaz Masalı' },
    { slug: 'sakli-bahce', name: 'Saklı Bahçe' },
    { slug: 'goc', name: 'Göç' },
  ];

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await fetch(`${backendUrl}/api/products`);
      if (res.ok) {
        const data = await res.json();
        setProducts(data);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProduct = async () => {
    setSaving(true);
    try {
      const productData = {
        name: newProduct.name,
        altName: newProduct.altName || null,
        price: parseInt(newProduct.price) || 0,
        material: newProduct.material,
        dimensions: newProduct.dimensions,
        description: newProduct.description,
        features: newProduct.features.split('\n').filter(f => f.trim()),
        colors: newProduct.colors.split(',').map(c => c.trim()).filter(c => c),
        sizes: newProduct.sizes.split(',').map(s => s.trim()).filter(s => s),
        collection: newProduct.collection,
        images: newProduct.images,
        isNew: newProduct.isNew,
        isBestseller: newProduct.isBestseller
      };

      const res = await fetch(`${backendUrl}/api/admin/products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(productData)
      });

      if (res.ok) {
        await fetchProducts();
        setShowCreateModal(false);
        setNewProduct({
          name: '', altName: '', price: 0, material: '%100 Has İpek',
          dimensions: '65x65 cm', description: '', features: '', colors: '',
          sizes: '65x65', collection: 'kokten-isiga', images: [], isNew: true, isBestseller: false
        });
        setMessage('✓ Ürün oluşturuldu');
      } else {
        const error = await res.json();
        setMessage(`Hata: ${error.detail || 'Ürün oluşturulamadı'}`);
      }
    } catch (error) {
      console.error('Error creating product:', error);
      setMessage('Hata: Ürün oluşturulamadı');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Bu ürünü silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.')) return;

    try {
      const res = await fetch(`${backendUrl}/api/admin/products/${productId}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        await fetchProducts();
        if (selectedProduct?.id === productId) {
          setSelectedProduct(null);
        }
        setMessage('✓ Ürün silindi');
      } else {
        const error = await res.json();
        setMessage(`Hata: ${error.detail || 'Ürün silinemedi'}`);
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      setMessage('Hata: Ürün silinemedi');
    }
  };

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = products.findIndex((p) => p.id === active.id);
      const newIndex = products.findIndex((p) => p.id === over.id);
      
      const newProducts = arrayMove(products, oldIndex, newIndex);
      setProducts(newProducts);

      // Save new order to backend
      try {
        const productIds = newProducts.map(p => p.id);
        await fetch(`${backendUrl}/api/admin/products/reorder`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(productIds)
        });
        setMessage('✓ Sıralama kaydedildi');
      } catch (error) {
        console.error('Error saving order:', error);
        setMessage('Hata: Sıralama kaydedilemedi');
      }
    }
  };

  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setEditData({
      name: product.name || '',
      altName: product.altName || '',
      price: product.price || 0,
      originalPrice: product.originalPrice || '',
      material: product.material || '',
      dimensions: product.dimensions || '',
      description: product.description || '',
      features: (product.features || []).join('\n'),
      colors: (product.colors || []).join(', '),
      sizes: (product.sizes || []).join(', '),
      prices: JSON.stringify(product.prices || {}, null, 2),
      collection: product.collection || 'kokten-isiga',
      isNew: product.isNew || false,
      isBestseller: product.isBestseller || false,
    });
    setEditMode(false);
    setMessage('');
  };

  const handleSave = async () => {
    if (!selectedProduct) return;
    
    setSaving(true);
    setMessage('');
    
    try {
      // Parse and prepare data
      const updateData = {
        name: editData.name,
        altName: editData.altName || null,
        price: parseInt(editData.price) || 0,
        originalPrice: editData.originalPrice ? parseInt(editData.originalPrice) : null,
        material: editData.material,
        dimensions: editData.dimensions,
        description: editData.description,
        features: editData.features.split('\n').filter(f => f.trim()),
        colors: editData.colors.split(',').map(c => c.trim()).filter(c => c),
        sizes: editData.sizes.split(',').map(s => s.trim()).filter(s => s),
        collection: editData.collection,
        isNew: editData.isNew,
        isBestseller: editData.isBestseller,
      };
      
      // Parse prices JSON
      try {
        updateData.prices = JSON.parse(editData.prices);
      } catch {
        setMessage('Fiyatlar JSON formatında olmalı');
        setSaving(false);
        return;
      }
      
      const res = await fetch(`${backendUrl}/api/admin/products/${selectedProduct.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });
      
      if (res.ok) {
        const result = await res.json();
        setMessage('✓ Ürün güncellendi');
        setEditMode(false);
        
        // Update local state
        if (result.product) {
          setProducts(products.map(p => p.id === selectedProduct.id ? result.product : p));
          setSelectedProduct(result.product);
        }
        
        // Refresh products
        await fetchProducts();
      } else {
        const error = await res.json();
        setMessage(`Hata: ${error.detail || 'Güncelleme başarısız'}`);
      }
    } catch (error) {
      console.error('Error saving product:', error);
      setMessage('Hata: Güncelleme başarısız');
    } finally {
      setSaving(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(price);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Ürün Yönetimi</h2>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">{products.length} ürün</span>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 bg-black text-white px-4 py-2 text-sm hover:bg-gray-800"
          >
            <Plus size={16} />
            Yeni Ürün
          </button>
        </div>
      </div>

      {message && (
        <div className={`p-3 rounded text-sm ${message.includes('✓') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {message}
        </div>
      )}

      {/* Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Products List with Drag and Drop */}
        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium">Ürünler</h3>
            <span className="text-xs text-gray-400">Sürükleyerek sıralayın</span>
          </div>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={products.map(p => p.id)}
              strategy={rectSortingStrategy}
            >
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {products.map((product) => (
                  <SortableProductCard
                    key={product.id}
                    product={product}
                    isSelected={selectedProduct?.id === product.id}
                    onSelect={handleSelectProduct}
                    onDelete={handleDeleteProduct}
                    formatPrice={formatPrice}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>

        {/* Product Details / Edit Form */}
        <div className="lg:col-span-2 bg-white border rounded-lg p-4">
          {selectedProduct ? (
            <>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium">
                  {editMode ? 'Ürünü Düzenle' : 'Ürün Detayları'}
                </h3>
                <div className="flex items-center gap-2">
                  {editMode ? (
                    <>
                      <button
                        onClick={() => setEditMode(false)}
                        className="px-3 py-1 text-sm border hover:bg-gray-50"
                      >
                        İptal
                      </button>
                      <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex items-center gap-1 px-3 py-1 text-sm bg-black text-white hover:bg-gray-800 disabled:bg-gray-400"
                      >
                        <Save size={14} />
                        {saving ? 'Kaydediliyor...' : 'Kaydet'}
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setEditMode(true)}
                      className="flex items-center gap-1 px-3 py-1 text-sm bg-black text-white hover:bg-gray-800"
                    >
                      <Edit2 size={14} />
                      Düzenle
                    </button>
                  )}
                </div>
              </div>

              {editMode ? (
                /* Edit Form */
                <div className="space-y-4 max-h-[450px] overflow-y-auto pr-2">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium mb-1">Ürün Adı</label>
                      <input
                        type="text"
                        value={editData.name}
                        onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                        className="w-full border px-3 py-2 text-sm"
                      />
                    </div>
                    
                    <div className="col-span-2">
                      <label className="block text-sm font-medium mb-1">Alt Başlık</label>
                      <input
                        type="text"
                        value={editData.altName}
                        onChange={(e) => setEditData({ ...editData, altName: e.target.value })}
                        className="w-full border px-3 py-2 text-sm"
                        placeholder="Opsiyonel"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-1">Fiyat (₺)</label>
                      <input
                        type="number"
                        value={editData.price}
                        onChange={(e) => setEditData({ ...editData, price: e.target.value })}
                        className="w-full border px-3 py-2 text-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-1">İndirimli Fiyat (₺)</label>
                      <input
                        type="number"
                        value={editData.originalPrice}
                        onChange={(e) => setEditData({ ...editData, originalPrice: e.target.value })}
                        className="w-full border px-3 py-2 text-sm"
                        placeholder="Opsiyonel"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-1">Materyal</label>
                      <input
                        type="text"
                        value={editData.material}
                        onChange={(e) => setEditData({ ...editData, material: e.target.value })}
                        className="w-full border px-3 py-2 text-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-1">Boyutlar</label>
                      <input
                        type="text"
                        value={editData.dimensions}
                        onChange={(e) => setEditData({ ...editData, dimensions: e.target.value })}
                        className="w-full border px-3 py-2 text-sm"
                      />
                    </div>
                    
                    <div className="col-span-2">
                      <label className="block text-sm font-medium mb-1">Açıklama</label>
                      <textarea
                        value={editData.description}
                        onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                        rows={3}
                        className="w-full border px-3 py-2 text-sm"
                      />
                    </div>
                    
                    <div className="col-span-2">
                      <label className="block text-sm font-medium mb-1">Özellikler (Her satıra bir özellik)</label>
                      <textarea
                        value={editData.features}
                        onChange={(e) => setEditData({ ...editData, features: e.target.value })}
                        rows={3}
                        className="w-full border px-3 py-2 text-sm font-mono"
                        placeholder="El Dikişi&#10;Özel Tasarım&#10;Cilt Dostu"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-1">Renkler (virgülle ayırın)</label>
                      <input
                        type="text"
                        value={editData.colors}
                        onChange={(e) => setEditData({ ...editData, colors: e.target.value })}
                        className="w-full border px-3 py-2 text-sm"
                        placeholder="Mor, Gümüş, Bej"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-1">Bedenler (virgülle ayırın)</label>
                      <input
                        type="text"
                        value={editData.sizes}
                        onChange={(e) => setEditData({ ...editData, sizes: e.target.value })}
                        className="w-full border px-3 py-2 text-sm"
                        placeholder="65x65, 90x90"
                      />
                    </div>
                    
                    <div className="col-span-2">
                      <label className="block text-sm font-medium mb-1">Beden Fiyatları (JSON)</label>
                      <textarea
                        value={editData.prices}
                        onChange={(e) => setEditData({ ...editData, prices: e.target.value })}
                        rows={4}
                        className="w-full border px-3 py-2 text-sm font-mono"
                        placeholder='{"65x65": 2000, "90x90": 2500}'
                      />
                    </div>
                    
                    <div className="col-span-2">
                      <label className="block text-sm font-medium mb-1">Koleksiyon *</label>
                      <select
                        value={editData.collection || 'kokten-isiga'}
                        onChange={(e) => setEditData({ ...editData, collection: e.target.value })}
                        className="w-full border px-3 py-2 text-sm bg-white"
                        data-testid="edit-product-collection"
                      >
                        {collectionOptions.map((c) => (
                          <option key={c.slug} value={c.slug}>{c.name}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={editData.isNew}
                          onChange={(e) => setEditData({ ...editData, isNew: e.target.checked })}
                          className="rounded"
                        />
                        <span className="text-sm">Yeni Ürün</span>
                      </label>
                      
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={editData.isBestseller}
                          onChange={(e) => setEditData({ ...editData, isBestseller: e.target.checked })}
                          className="rounded"
                        />
                        <span className="text-sm">Çok Satan</span>
                      </label>
                    </div>
                  </div>
                </div>
              ) : (
                /* View Mode */
                <div className="space-y-4 max-h-[450px] overflow-y-auto">
                  <div className="flex gap-4">
                    <img
                      src={selectedProduct.image}
                      alt={selectedProduct.name}
                      className="w-32 h-32 object-cover rounded"
                    />
                    <div>
                      <h4 className="font-semibold text-lg">{selectedProduct.name}</h4>
                      {selectedProduct.altName && (
                        <p className="text-gray-500 text-sm">{selectedProduct.altName}</p>
                      )}
                      <p className="text-xl font-bold mt-2">{formatPrice(selectedProduct.price)}</p>
                      {selectedProduct.originalPrice && (
                        <p className="text-gray-400 line-through text-sm">
                          {formatPrice(selectedProduct.originalPrice)}
                        </p>
                      )}
                      <div className="flex gap-2 mt-2">
                        {selectedProduct.isNew && (
                          <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">Yeni</span>
                        )}
                        {selectedProduct.isBestseller && (
                          <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded">Çok Satan</span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Materyal</p>
                      <p className="font-medium">{selectedProduct.material}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Boyutlar</p>
                      <p className="font-medium">{selectedProduct.dimensions}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Renkler</p>
                      <p className="font-medium">{(selectedProduct.colors || []).join(', ')}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Bedenler</p>
                      <p className="font-medium">{(selectedProduct.sizes || []).join(', ')}</p>
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-gray-500 text-sm">Açıklama</p>
                    <p className="text-sm mt-1">{selectedProduct.description}</p>
                  </div>
                  
                  <div>
                    <p className="text-gray-500 text-sm">Özellikler</p>
                    <ul className="list-disc list-inside text-sm mt-1">
                      {(selectedProduct.features || []).map((f, i) => (
                        <li key={i}>{f}</li>
                      ))}
                    </ul>
                  </div>
                  
                  <div>
                    <p className="text-gray-500 text-sm">Beden Fiyatları</p>
                    <div className="flex gap-2 flex-wrap mt-1">
                      {Object.entries(selectedProduct.prices || {}).map(([size, price]) => (
                        <span key={size} className="bg-gray-100 px-2 py-1 rounded text-sm">
                          {size}: {formatPrice(price)}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              Düzenlemek için bir ürün seçin
            </div>
          )}
        </div>
      </div>

      {/* Create Product Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Yeni Ürün Oluştur</h3>
              <button onClick={() => setShowCreateModal(false)}>
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Ürün Adı *</label>
                  <input
                    type="text"
                    value={newProduct.name}
                    onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                    className="w-full border px-3 py-2 text-sm"
                    placeholder="Ürün adı"
                  />
                </div>
                
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Alt Başlık</label>
                  <input
                    type="text"
                    value={newProduct.altName}
                    onChange={(e) => setNewProduct({ ...newProduct, altName: e.target.value })}
                    className="w-full border px-3 py-2 text-sm"
                    placeholder="Opsiyonel"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Fiyat (₺) *</label>
                  <input
                    type="number"
                    value={newProduct.price}
                    onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })}
                    className="w-full border px-3 py-2 text-sm"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Materyal</label>
                  <input
                    type="text"
                    value={newProduct.material}
                    onChange={(e) => setNewProduct({ ...newProduct, material: e.target.value })}
                    className="w-full border px-3 py-2 text-sm"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Boyutlar</label>
                  <input
                    type="text"
                    value={newProduct.dimensions}
                    onChange={(e) => setNewProduct({ ...newProduct, dimensions: e.target.value })}
                    className="w-full border px-3 py-2 text-sm"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Bedenler (virgülle ayırın)</label>
                  <input
                    type="text"
                    value={newProduct.sizes}
                    onChange={(e) => setNewProduct({ ...newProduct, sizes: e.target.value })}
                    className="w-full border px-3 py-2 text-sm"
                    placeholder="65x65, 90x90"
                  />
                </div>
                
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Koleksiyon *</label>
                  <select
                    value={newProduct.collection}
                    onChange={(e) => setNewProduct({ ...newProduct, collection: e.target.value })}
                    className="w-full border px-3 py-2 text-sm bg-white"
                    data-testid="new-product-collection"
                  >
                    {collectionOptions.map((c) => (
                      <option key={c.slug} value={c.slug}>{c.name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Ürünün ait olacağı koleksiyonu seçin. Ürün, koleksiyon sayfasında da listelenir.</p>
                </div>
                
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Açıklama</label>
                  <textarea
                    value={newProduct.description}
                    onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                    rows={2}
                    className="w-full border px-3 py-2 text-sm"
                  />
                </div>
                
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Özellikler (Her satıra bir özellik)</label>
                  <textarea
                    value={newProduct.features}
                    onChange={(e) => setNewProduct({ ...newProduct, features: e.target.value })}
                    rows={2}
                    className="w-full border px-3 py-2 text-sm"
                    placeholder="El Dikişi&#10;Özel Tasarım"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Renkler (virgülle ayırın)</label>
                  <input
                    type="text"
                    value={newProduct.colors}
                    onChange={(e) => setNewProduct({ ...newProduct, colors: e.target.value })}
                    className="w-full border px-3 py-2 text-sm"
                    placeholder="Mor, Gümüş"
                  />
                </div>
                
                <div className="flex items-center gap-4 pt-6">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={newProduct.isNew}
                      onChange={(e) => setNewProduct({ ...newProduct, isNew: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm">Yeni Ürün</span>
                  </label>
                  
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={newProduct.isBestseller}
                      onChange={(e) => setNewProduct({ ...newProduct, isBestseller: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm">Çok Satan</span>
                  </label>
                </div>
                
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-2">Ürün Görselleri</label>
                  <FileUploader
                    accept="image/*"
                    label="Görsel Yükle"
                    onUpload={(url) => setNewProduct({ ...newProduct, images: [...newProduct.images, url] })}
                  />
                  {newProduct.images.length > 0 && (
                    <div className="flex gap-2 flex-wrap mt-2">
                      {newProduct.images.map((img, i) => (
                        <div key={i} className="relative">
                          <img src={getImageUrl(img)} alt={`Görsel ${i+1}`} className="w-16 h-16 object-cover rounded" />
                          <button
                            onClick={() => setNewProduct({ 
                              ...newProduct, 
                              images: newProduct.images.filter((_, idx) => idx !== i) 
                            })}
                            className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-4 border-t">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-sm border hover:bg-gray-50"
                >
                  İptal
                </button>
                <button
                  onClick={handleCreateProduct}
                  disabled={!newProduct.name || !newProduct.price || saving}
                  className="px-4 py-2 text-sm bg-black text-white hover:bg-gray-800 disabled:bg-gray-300"
                >
                  {saving ? 'Oluşturuluyor...' : 'Ürün Oluştur'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductManager;
